/**
 * PWA Install Prompt Component
 * 
 * 引導用戶安裝 PWA 到手機桌面
 * 支援 iOS Safari 和 Android Chrome 等瀏覽器
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  PhoneIphone as PhoneIphoneIcon,
  Android as AndroidIcon,
  ArrowForward as ArrowForwardIcon,
  Share as ShareIcon,
  AddBox as AddBoxIcon,
  Menu as MenuIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { getItem, setItem } from '../../utils/localStorage';
import {
  APP_INSTALLED_EVENT,
  INSTALL_PROMPT_AVAILABLE_EVENT,
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
} from '../../utils/pwaInstall';

const STORAGE_KEY = 'pwaInstallPromptDismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7天

// 檢測是否為移動設備
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// 檢測是否已安裝 PWA
const isPWAInstalled = () => {
  // 方法1: 檢測 display-mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // 方法2: 檢測 iOS standalone 模式
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // 方法3: 檢測 Android 從主屏幕啟動
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

// 檢測瀏覽器類型
const detectBrowser = () => {
  const userAgent = navigator.userAgent;
  
  // 優先檢測 iOS（必須在 Android 之前）
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  
  // 檢測 Android
  if (/Android/.test(userAgent)) {
    // Android 設備上的 Chrome
    if (/Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor)) {
      return 'android'; // Android Chrome 會觸發 beforeinstallprompt
    }
    return 'android'; // 其他 Android 瀏覽器
  }
  
  // 檢測桌面 Chrome
  if (/Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor)) {
    return 'chrome';
  }
  
  // 檢測 Edge
  if (/Edg/.test(userAgent)) {
    return 'edge';
  }
  
  // 檢測 Firefox
  if (/Firefox/.test(userAgent)) {
    return 'firefox';
  }
  
  // 檢測 Safari (桌面)
  if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
    return 'safari';
  }
  
  return 'other';
};


const PWAInstallPrompt = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredInstallPrompt());
  const [browser, setBrowser] = useState('other');
  const [isMobile, setIsMobile] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  const isTC = language === 'tc';

  // 文本內容
  const texts = {
    tc: {
      title: '安裝應用程式',
      subtitle: '將應用程式添加到主屏幕，隨時快速存取',
      androidTitle: 'Android 安裝步驟',
      iosTitle: 'iOS 安裝步驟',
      installButton: '安裝應用程式',
      dismissButton: '稍後提醒',
      closeButton: '關閉',
      alreadyInstalled: '應用程式已安裝',
      notSupported: '您的瀏覽器不支援安裝功能',
      step1: '點擊瀏覽器選單',
      step2: '選擇「安裝應用程式（Install app）」',
      step3: '確認安裝',
      step4: '完成！應用程式已添加到主屏幕',
      androidHint: '如果只看到「加入主畫面」，通常只會建立捷徑。請使用 Chrome 的「安裝應用程式」。',
      waitingNativePrompt: '尚未取得原生安裝提示。請先在 Chrome 瀏覽幾秒並互動後再按安裝。',
      iosStep1: '點擊底部的分享按鈕',
      iosStep2: '向下滾動並選擇「加入主畫面」',
      iosStep3: '點擊「加入」確認',
      iosStep4: '完成！應用程式已添加到主屏幕',
      chromeStep1: '點擊瀏覽器右上角的三點選單',
      chromeStep2: '選擇「安裝應用程式」或「安裝 [應用名稱]」',
      chromeStep3: '在彈出對話框中點擊「安裝」',
      chromeStep4: '完成！應用程式已添加到主屏幕',
    },
    en: {
      title: 'Install App',
      subtitle: 'Add the app to your home screen for quick access',
      androidTitle: 'Android Installation Steps',
      iosTitle: 'iOS Installation Steps',
      installButton: 'Install App',
      dismissButton: 'Remind Me Later',
      closeButton: 'Close',
      alreadyInstalled: 'App is already installed',
      notSupported: 'Your browser does not support installation',
      step1: 'Tap the browser menu',
      step2: 'Select "Install app"',
      step3: 'Confirm installation',
      step4: 'Done! App has been added to home screen',
      androidHint: 'If you only choose "Add to Home screen", Chrome may create a shortcut instead of installing the app.',
      waitingNativePrompt: 'Native install prompt is not ready yet. Browse and interact in Chrome for a while, then try again.',
      iosStep1: 'Tap the Share button at the bottom',
      iosStep2: 'Scroll down and select "Add to Home Screen"',
      iosStep3: 'Tap "Add" to confirm',
      iosStep4: 'Done! App has been added to home screen',
      chromeStep1: 'Tap the three-dot menu in the browser',
      chromeStep2: 'Select "Install App" or "Install [App Name]"',
      chromeStep3: 'Tap "Install" in the popup dialog',
      chromeStep4: 'Done! App has been added to home screen',
    },
  };

  const t = texts[language] || texts.en;

  // 監聽來自 Banner 的事件
  useEffect(() => {
    const handleShowDialog = () => {
      setOpen(true);
    };

    window.addEventListener('pwa-install-show-dialog', handleShowDialog);

    return () => {
      window.removeEventListener('pwa-install-show-dialog', handleShowDialog);
    };
  }, []);

  // 初始化檢測
  useEffect(() => {
    const checkInstallStatus = () => {
      const mobile = isMobileDevice();
      const installed = isPWAInstalled();
      const browserType = detectBrowser();
      const dismissed = getItem(STORAGE_KEY);
      
      setIsMobile(mobile);
      setIsInstalled(installed);
      setBrowser(browserType);

      // 如果已安裝，不顯示提示
      if (installed) {
        return;
      }

      // 如果用戶在7天內已經關閉過提示，不顯示
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const now = Date.now();
        if (now - dismissedTime < DISMISS_DURATION) {
          return;
        }
      }

      // 只在移動設備上顯示提示（但不在 banner 顯示時自動顯示對話框）
      // 對話框現在只在用戶點擊 banner 的安裝按鈕時顯示
    };

    checkInstallStatus();
  }, []);

  // 同步全局 install prompt 事件
  useEffect(() => {
    const syncDeferredPrompt = () => {
      setDeferredPrompt(getDeferredInstallPrompt());
      setOpen(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setOpen(false);
      setIsInstalled(true);
    };

    // 元件掛載時先同步一次，避免錯過早期事件
    setDeferredPrompt(getDeferredInstallPrompt());

    window.addEventListener(INSTALL_PROMPT_AVAILABLE_EVENT, syncDeferredPrompt);
    window.addEventListener(APP_INSTALLED_EVENT, handleAppInstalled);

    return () => {
      window.removeEventListener(INSTALL_PROMPT_AVAILABLE_EVENT, syncDeferredPrompt);
      window.removeEventListener(APP_INSTALLED_EVENT, handleAppInstalled);
    };
  }, []);

  // 處理安裝按鈕點擊（Android Chrome）
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    try {
      // 顯示安裝提示
      deferredPrompt.prompt();

      // 等待用戶回應
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setOpen(false);
        setIsInstalled(true);
      } else {
        console.log('User dismissed the install prompt');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Install prompt failed, fallback to manual instructions:', error);
      }
    } finally {
      // 清除保存的提示
      clearDeferredInstallPrompt();
      setDeferredPrompt(null);
    }
  };

  // 處理關閉
  const handleClose = (dismiss = false) => {
    setOpen(false);
    if (dismiss) {
      // 記錄用戶選擇稍後提醒
      setItem(STORAGE_KEY, Date.now().toString());
    }
  };

  // 獲取安裝步驟
  const getInstallSteps = () => {
    if (browser === 'ios') {
      return [
        { label: t.iosStep1, icon: <ShareIcon /> },
        { label: t.iosStep2, icon: <AddBoxIcon /> },
        { label: t.iosStep3, icon: <ArrowForwardIcon /> },
        { label: t.iosStep4, icon: <CheckCircleIcon /> },
      ];
    } else if (browser === 'android') {
      return [
        { label: t.step1, icon: <MenuIcon /> },
        { label: t.step2, icon: <AddBoxIcon /> },
        { label: t.step3, icon: <ArrowForwardIcon /> },
        { label: t.step4, icon: <CheckCircleIcon /> },
      ];
    } else if (browser === 'chrome' && isMobile) {
      return [
        { label: t.chromeStep1, icon: <MenuIcon /> },
        { label: t.chromeStep2, icon: <GetAppIcon /> },
        { label: t.chromeStep3, icon: <ArrowForwardIcon /> },
        { label: t.chromeStep4, icon: <CheckCircleIcon /> },
      ];
    }
    return [];
  };

  // 如果已安裝，不顯示
  if (isInstalled) {
    return null;
  }

  // 如果不是移動設備，不顯示
  if (!isMobile) {
    return null;
  }

  const steps = getInstallSteps();
  const supportsInstallPrompt = typeof window !== 'undefined' && 'onbeforeinstallprompt' in window;
  // Android Chrome 支援原生安裝提示
  const showNativeInstallButton = deferredPrompt !== null;

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {browser === 'ios' ? (
              <PhoneIphoneIcon color="primary" />
            ) : (
              <AndroidIcon color="primary" />
            )}
            <Typography variant="h6">{t.title}</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => handleClose(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t.subtitle}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {showNativeInstallButton && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {isTC
              ? '您的瀏覽器支援一鍵安裝，點擊下方按鈕即可安裝應用程式。或者按照下方步驟手動安裝。'
              : 'Your browser supports one-click installation. Tap the button below to install the app, or follow the steps below to install manually.'}
          </Alert>
        )}
        {browser === 'android' && !showNativeInstallButton && supportsInstallPrompt && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t.waitingNativePrompt}
          </Alert>
        )}
        {browser === 'android' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t.androidHint}
          </Alert>
        )}
        {steps.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {browser === 'ios' ? t.iosTitle : t.androidTitle}
            </Typography>
            <Stepper activeStep={-1} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {step.icon}
                      </Box>
                    )}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        ) : (
          <Alert severity="warning">
            {t.notSupported}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {showNativeInstallButton ? (
          <>
            <Button onClick={() => handleClose(true)} color="inherit">
              {t.dismissButton}
            </Button>
            <Button
              onClick={handleInstallClick}
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              autoFocus
            >
              {t.installButton}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => handleClose(true)} color="inherit">
              {t.dismissButton}
            </Button>
            <Button onClick={() => handleClose(false)} variant="contained" color="primary">
              {t.closeButton}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PWAInstallPrompt;
